import {
  createSearchParams,
  getSearchParamsForLocation,
  URLSearchParamsInit,
} from "@/lib/router-helpers"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useMemo, useRef } from "react"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/

type SetURLSearchParams = (
  nextInit?:
    | URLSearchParamsInit
    | ((prev: URLSearchParams) => URLSearchParamsInit),
  options?: {
    scroll?: boolean
    replace?: boolean
  },
) => void

/* -------------------------------------------------------------------------------------------------
 * hook
 * -----------------------------------------------------------------------------------------------*/

export function useCustomSearchParams(
  defaultInit?: URLSearchParamsInit,
): [URLSearchParams, SetURLSearchParams] {
  const _nextSearchParams = useSearchParams()
  const pathName = usePathname()
  const router = useRouter()

  const defaultSearchParamsRef = useRef(createSearchParams(defaultInit))
  const hasSetSearchParamsRef = useRef(false)

  const searchParams = useMemo(
    () =>
      getSearchParamsForLocation(
        _nextSearchParams.toString(),
        // eslint-disable-next-line react-hooks/refs
        hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current,
      ),
    [_nextSearchParams],
  )

  const setSearchParams = useCallback<SetURLSearchParams>(
    (nextInit, options = { replace: true }) => {
      const newSearchParams = createSearchParams(
        typeof nextInit === "function" ? nextInit(searchParams) : nextInit,
      )
      hasSetSearchParamsRef.current = true

      if (options?.replace)
        router.replace(`${pathName}?${newSearchParams}`, {
          scroll: options.scroll,
        })
      else
        router.push(`${pathName}?${newSearchParams}`, {
          scroll: options?.scroll,
        })
    },
    [searchParams, pathName, router],
  )

  return [searchParams, setSearchParams]
}
